/* SkillConnect — Capacitor runtime helpers.
 *
 * Loaded everywhere (web + native). On web it short-circuits to "I'm not
 * native", on iOS/Android it wires the native shell (status bar colour,
 * Android hardware back-button → router, splash hide).
 *
 * Stays tree-shakeable: the plugin imports are static so webpack can elide
 * them if they happen to be unused, but their tiny size justifies always
 * including them. No SSR risk — index.js loads us after createRoot().
 */
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";

export const isNativeApp = () =>
  typeof window !== "undefined" && Capacitor?.isNativePlatform?.() === true;

export const nativePlatform = () =>
  typeof window !== "undefined" ? (Capacitor?.getPlatform?.() || "web") : "web";

/**
 * Initialise the native shell. Safe to call unconditionally — bails out on
 * web. Returns a cleanup function for any listeners attached.
 */
export async function initNativeShell() {
  if (!isNativeApp()) return () => {};

  // 1) Status bar colour matches our brand
  try {
    await StatusBar.setBackgroundColor({ color: "#0040A8" });
    await StatusBar.setStyle({ style: Style.Dark });
  } catch { /* unsupported on some Androids */ }

  // 2) Hide the splash once React has mounted
  try { await SplashScreen.hide(); } catch { /* noop */ }

  // 3) Android hardware back: pop history, exit if at root
  const handle = await CapApp.addListener("backButton", ({ canGoBack }) => {
    if (canGoBack && window.history.length > 1) {
      window.history.back();
    } else {
      CapApp.exitApp();
    }
  });

  return () => { handle.remove(); };
}
